/**
 * Client-side helpers for GET /bank/resolve-armenian autofill.
 * The backend owns the CBA prefix table; these only decide when to call and
 * whether applying the result would overwrite a manual edit.
 */

export interface ArmenianBankResolve {
  code: string;
  name: string;
  swift: string;
}

/** Digits only after stripping spaces/dashes; drop AM + check digits for pseudo-IBAN. */
export function normalizeArmenianAccountNumber(raw: string | null | undefined): string {
  if (raw == null) return '';
  const compact = String(raw).replace(/[\s-]/g, '').toUpperCase();
  if (!compact) return '';
  if (compact.startsWith('AM') && compact.length > 4) {
    return compact.slice(4).replace(/\D/g, '');
  }
  return compact.replace(/\D/g, '');
}

/** Enough digits for a CBA bank code, or an AM… pseudo-IBAN. */
export function looksLikeArmenianAccount(
  accountNumber: string | null | undefined
): boolean {
  if (accountNumber == null) return false;
  const raw = String(accountNumber).replace(/[\s-]/g, '').toUpperCase();
  if (raw.startsWith('AM') && raw.length > 4) return true;
  return normalizeArmenianAccountNumber(accountNumber).length >= 5;
}

function isArmenianCountry(bankCountry?: string | null): boolean {
  const c = (bankCountry || '').trim().toUpperCase();
  return c === 'AM' || c === 'ARM' || c === 'ARMENIA';
}

/**
 * Whether to call resolve-armenian for this form state.
 * Only when destination/bank country is Armenia (or ARM withdrawal view) and
 * the account has enough digits / looks like an AM IBAN.
 */
export function shouldResolveArmenianBank(opts: {
  accountNumber?: string | null;
  bankCountry?: string | null;
  viewType?: string | null;
}): boolean {
  const account = opts.accountNumber?.trim() ?? '';
  if (!account) return false;

  const isArmenia =
    opts.viewType === 'arm' || isArmenianCountry(opts.bankCountry);
  if (!isArmenia) return false;

  return looksLikeArmenianAccount(account);
}

export interface ArmenianBankAutofillInput {
  bank: ArmenianBankResolve | null | undefined;
  currentName: string;
  currentSwift?: string;
  lastAutofilledName?: string | null;
  lastAutofilledSwift?: string | null;
}

export interface ArmenianBankAutofillResult {
  bankName?: string;
  swift?: string;
  lastAutofilledName?: string;
  lastAutofilledSwift?: string;
}

/**
 * Returns only fields that should be written. Empty result means leave the form alone.
 * Bank name and SWIFT are always overwritten when a valid bank is resolved,
 * since the resolved value is authoritative.
 */
export function applyArmenianBankAutofill(
  input: ArmenianBankAutofillInput
): ArmenianBankAutofillResult {
  debugger;
  const bank = input.bank;
  if (!bank?.name) return {};

  const out: ArmenianBankAutofillResult = {};

  out.bankName = bank.name;
  out.lastAutofilledName = bank.name;

  if (bank.swift && input.currentSwift !== undefined) {
    out.swift = bank.swift;
    out.lastAutofilledSwift = bank.swift;
  }

  return out;
}
